// text-anim.js
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export default function initTextAnims() {
  const SELECTOR =
    ".rich-text h1, .rich-text h2, .rich-text h3, .rich-text h4, .rich-text h5, .title-anim";

  const instances = new Map();
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let lastW = window.innerWidth;
  let lastOrientationPortrait = window.matchMedia("(orientation: portrait)").matches;
  let lastDPR = window.devicePixelRatio || 1;

  function isLayoutAffectingResize() {
    const w = window.innerWidth;
    const orientationPortrait = window.matchMedia("(orientation: portrait)").matches;
    const dpr = window.devicePixelRatio || 1;

    const widthChanged = Math.abs(w - lastW) >= 2;
    const orientationChanged = orientationPortrait !== lastOrientationPortrait;
    const dprChanged = dpr !== lastDPR;

    lastW = w;
    lastOrientationPortrait = orientationPortrait;
    lastDPR = dpr;

    return widthChanged || orientationChanged || dprChanged;
  }

  // --- Utils ---
  function debounce(fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function markPending() {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      el.classList.add("ta-pending");
    });
  }

  // Load exactly the fonts used by the target elements before splitting.
  async function waitForFontsForTargets(targets, { timeout = 4000 } = {}) {
    if (!("fonts" in document) || typeof document.fonts.load !== "function") {
      // Best-effort fallback for older browsers
      await new Promise((r) => setTimeout(r, 300));
      return;
    }

    const fontPromises = new Set();

    targets.forEach((el) => {
      const cs = window.getComputedStyle(el);
      // Build a CSS font shorthand so FontFaceSet can load the exact face
      const style = cs.fontStyle || "normal";
      const weight = cs.fontWeight || "400";
      const stretch = cs.fontStretch || "normal";
      const size = cs.fontSize || "16px";
      const lineHeight = cs.lineHeight && cs.lineHeight !== "normal" ? `/${cs.lineHeight}` : "";
      const families = (cs.fontFamily || "").split(",").map((f) => f.trim().replace(/^["']|["']$/g, "")).filter(Boolean);

      families.forEach((family) => {
        // Use a representative glyph set for better loading across subsets
        const fontShorthand = `${style} ${variantFromWeight(weight)} ${stretch} ${size}${lineHeight} "${family}"`;
        // Load with two texts to encourage Latin + extended subsets if present
        fontPromises.add(document.fonts.load(fontShorthand, "ABCDEabcde0123"));
        fontPromises.add(document.fonts.load(fontShorthand, "éçàùÉÇÀÙœæß"));
      });
    });

    const allLoads = Promise.allSettled(Array.from(fontPromises))
      .then(() => document.fonts.ready) // ensure the set is marked ready
      .catch(() => {});

    // Safety timeout so we don't hang forever on flaky faces
    const timed = new Promise((resolve) => setTimeout(resolve, timeout));

    await Promise.race([allLoads, timed]);
    // One extra frame so layout settles before SplitText measures
    await new Promise((r) => requestAnimationFrame(() => r()));
  }

  function variantFromWeight(w) {
    // Normalize font-weight to numeric for the shorthand
    if (w === "bold") return 700;
    if (w === "normal") return 400;
    const n = parseInt(w, 10);
    return Number.isNaN(n) ? 400 : n;
  }

  function prepareElement(el) {
    if (el.__taPrepared) return instances.get(el);

    // Split words
    const split = new SplitText(el, { type: "words" });

    // Wrap + set initial transforms
    split.words.forEach((word) => {
      if (word.querySelector(".ta-inner")) return;

      word.classList.add("ta-wrap");
      Object.assign(word.style, {
        display: "inline-block",
        overflow: "hidden",
        verticalAlign: "top",
      });

      const inner = document.createElement("span");
      inner.className = "ta-inner";
      while (word.firstChild) inner.appendChild(word.firstChild);
      word.appendChild(inner);

      Object.assign(inner.style, { display: "inline-block", willChange: "transform" });
      gsap.set(inner, { yPercent: 100 });
    });

    el.classList.remove("ta-pending");
    el.classList.add("ta-ready");

    // Scroll animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 85%",
        once: true,
        invalidateOnRefresh: true,
        onEnter: prefersReduced ? (self) => self.disable() : undefined,
      },
    });

    if (!prefersReduced) {
      tl.to(el.querySelectorAll(".ta-inner"), {
        yPercent: 0,
        duration: 0.8,
        ease: "back.out(1.4)",
        stagger: { each: 0.04, from: "start" },
        clearProps: "transform",
      });
    } else {
      // Reduced motion: final state
      gsap.set(el.querySelectorAll(".ta-inner"), { yPercent: 0, clearProps: "transform" });
    }

    el.__taPrepared = true;
    return { split, tl };
  }

  function initTitleAnim({ selector = SELECTOR } = {}) {
    document.querySelectorAll(selector).forEach((el) => {
      if (instances.has(el)) return;
      const inst = prepareElement(el);
      instances.set(el, inst);
    });
  }

  function refreshTitleAnim() {
    instances.forEach(({ split, tl }, el) => {
      if (tl) tl.kill();
      if (split) split.revert();
      el.__taPrepared = false;
      el.classList.add("ta-pending");
      el.classList.remove("ta-ready");
    });
    instances.clear();
    initTitleAnim({});
    ScrollTrigger.refresh();
  }

  function destroyTitleAnim() {
    instances.forEach(({ split, tl }, el) => {
      if (tl) tl.kill();
      if (split) split.revert();
      el.__taPrepared = false;
      el.classList.remove("ta-pending", "ta-ready");
    });
    instances.clear();
    ScrollTrigger.refresh();
  }

  // 1) mark pending
  markPending();

  // 2) wait for the actual fonts used by the targets, then init
  const runInit = async () => {
    const targets = Array.from(document.querySelectorAll(SELECTOR));
    await waitForFontsForTargets(targets, { timeout: 4500 });
    initTitleAnim();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runInit, { once: true });
  } else {
    runInit();
  }

  // 3) Re-split on resize only when layout likely changed
  const onResize = debounce(() => {
    if (!isLayoutAffectingResize()) return;
    if (!prefersReduced) {
      refreshTitleAnim();
    } else {
      ScrollTrigger.refresh();
    }
  }, 200);

  window.addEventListener("resize", onResize);

  // Expose API
  return { init: initTitleAnim, refresh: refreshTitleAnim, destroy: destroyTitleAnim };
}
